import { useState, type FormEvent } from 'react';
import { backend } from '../data';
import { t, tErr } from '@bhorosha/i18n';
import { AuthCard, ErrorText, Field, Notice, PasswordInput, PasswordStrength } from '../ui/components';

type Mode = 'signin' | 'signup' | 'forgot' | 'checkEmail' | 'resetSent';

export function AuthScreen({ presetEmail, header }: { presetEmail?: string; header?: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState(presetEmail || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);

  const go = (m: Mode) => { setErr(''); setMode(m); };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    if (mode === 'signup' && password.length < 8) { setErr(t('auth.err.short')); return; }
    setBusy(true);
    try {
      if (mode === 'signin') await backend.signIn(email.trim(), password);
      else if (mode === 'signup') {
        const r = await backend.signUp(email.trim(), password, name.trim());
        if (r.needsConfirmation) setMode('checkEmail');
      } else if (mode === 'forgot') {
        await backend.sendPasswordReset(email.trim());
        setMode('resetSent');
      }
    } catch (ex) {
      setErr(tErr(ex));
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'checkEmail' || mode === 'resetSent') {
    return (
      <AuthCard>
        {header}
        <h1 className="bh-h1">{mode === 'checkEmail' ? t('auth.checkEmailTitle') : t('auth.forgot')}</h1>
        <Notice tone="ok">{mode === 'checkEmail' ? t('auth.checkEmail', { email }) : t('auth.resetSent', { email })}</Notice>
        <button className="bh-btn bh-btn-ghost bh-full" onClick={() => go('signin')}>{t('auth.signIn')}</button>
      </AuthCard>
    );
  }

  const title = mode === 'signin' ? t('auth.welcome') : mode === 'signup' ? t('auth.createTitle') : t('auth.forgot');
  const sub = mode === 'signin' ? t('auth.welcomeSub') : mode === 'signup' ? t('auth.createSub') : t('auth.forgotSub');
  return (
    <AuthCard>
      {header}
      <h1 className="bh-h1 bh-auth-title">{title}</h1>
      <p className="bh-auth-sub">{sub}</p>
      <form onSubmit={submit} className="bh-form">
        {mode === 'signup' && (
          <Field label={t('auth.name')}>
            <input value={name} onChange={e => setName(e.target.value)} placeholder={t('auth.namePlaceholder')} autoComplete="name" required />
          </Field>
        )}
        <Field label={t('auth.email')}>
          {/* Sign in also accepts a phone-login user name (made by the family owner). */}
          <input type={mode === 'signin' ? 'text' : 'email'} value={email} onChange={e => setEmail(e.target.value)} autoComplete={mode === 'signin' ? 'username' : 'email'}
            autoCapitalize="none" placeholder={mode === 'signin' ? t('auth.emailOrUser') : 'you@example.com'} required autoFocus={!presetEmail} />
        </Field>
        {mode !== 'forgot' && (
          <Field label={t('auth.password')} hint={mode === 'signup' ? t('auth.passwordHint') : undefined}>
            <span className="bh-pw">
              <input type={showPw ? 'text' : 'password'} aria-label={t('auth.password')} value={password} onChange={e => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required minLength={mode === 'signup' ? 8 : undefined} />
              <button type="button" className="bh-pw-eye" onClick={() => setShowPw(v => !v)} aria-label={showPw ? t('auth.hidePw') : t('auth.showPw')} title={showPw ? t('auth.hidePw') : t('auth.showPw')}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />{showPw && <path d="M3 3l18 18" />}
                </svg>
              </button>
            </span>
            {mode === 'signup' && <PasswordStrength value={password} />}
          </Field>
        )}
        {mode === 'signin' && (
          <div className="bh-auth-row"><button type="button" className="bh-link" onClick={() => go('forgot')}>{t('auth.forgot')}</button></div>
        )}
        <ErrorText>{err}</ErrorText>
        <button className="bh-btn bh-btn-primary bh-full bh-btn-lg" disabled={busy} type="submit">
          {busy ? t('common.loading') : mode === 'signin' ? t('auth.signIn') : mode === 'signup' ? t('auth.signUp') : t('auth.sendReset')}
        </button>
      </form>
      <div className="bh-auth-foot">
        {mode === 'signin' && <span>{t('auth.inviteOnly')} <a className="bh-link" href="/manual" target="_blank" rel="noreferrer">{t('auth.howItWorks')}</a></span>}
        {mode === 'signup' && <span>{t('auth.haveAccount')} <button className="bh-link" onClick={() => go('signin')}>{t('auth.signIn')}</button></span>}
        {mode === 'forgot' && <button className="bh-link" onClick={() => go('signin')}>{t('common.back')}</button>}
      </div>
    </AuthCard>
  );
}

export function ResetPasswordScreen({ onDone }: { onDone: () => void }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pw.length < 8) { setErr(t('auth.err.short')); return; }
    setBusy(true); setErr('');
    try { await backend.updatePassword(pw); onDone(); }
    catch (ex) { setErr(tErr(ex)); }
    finally { setBusy(false); }
  }
  return (
    <AuthCard>
      <h1 className="bh-h1">{t('auth.setNewPassword')}</h1>
      <form onSubmit={submit} className="bh-form">
        <Field label={t('auth.passwordNew')}>
          <PasswordInput value={pw} onChange={setPw} autoFocus strength />
        </Field>
        <ErrorText>{err}</ErrorText>
        <button className="bh-btn bh-btn-primary bh-full" disabled={busy}>{t('common.save')}</button>
      </form>
    </AuthCard>
  );
}
